import React, { useState } from 'react';

import advocatesProLogo from 'images/advpro_launch/advocatespro_logo.png';
import videoPoster from 'images/advpro_launch/poster.jpg';

import {
  ADVPRO_LAUNCH_FORM_URL,
  ADVPRO_LAUNCH_VIDEO_EMBED_URL
} from 'constants/advproLaunch.constants';

// Diseño tomado de modal-advocatespro-horizontal.html (lanzamiento AdvPro,
// webinar del miércoles 5 de agosto de 2026). Íconos de Tabler/Lucide como SVG
// inline (la font de tabler del mockup pesa ~2MB).

const PINK = '#e6197f';
const PINK_DARK = '#c9146d';
const URBANIST = "'Urbanist', sans-serif";
const DMSANS = "'DM Sans', sans-serif";

const iconProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const
};

const CloseIcon = () => (
  <svg {...iconProps} width={14} height={14}>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const TrophyIcon = () => (
  <svg {...iconProps}>
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

const SparklesIcon = () => (
  <svg {...iconProps}>
    <path d="M12 3l1.9 5.7a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z" />
  </svg>
);

const ChartBarIcon = () => (
  <svg {...iconProps}>
    <path d="M6 20v-4M12 20V10M18 20V4" />
  </svg>
);

const DeviceMobileIcon = () => (
  <svg {...iconProps}>
    <rect x="6" y="2" width="12" height="20" rx="2" />
    <path d="M12 18h.01" />
  </svg>
);

const TicketIcon = () => (
  <svg {...iconProps}>
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
    <path d="M13 5v2M13 11v2M13 17v2" />
  </svg>
);

const PlayIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill={PINK}>
    <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86a1 1 0 0 0-1.5.86z" />
  </svg>
);

interface Feature {
  icon: () => JSX.Element;
  title: string;
  text: string;
}

const FEATURES: Feature[] = [
  {
    icon: TrophyIcon,
    title: 'Gamificación completa',
    text: 'Badges, niveles y ranking en tiempo real para tus embajadores.'
  },
  {
    icon: SparklesIcon,
    title: 'IA entrenada en tu marca',
    text:
      'Genera propuestas de campañas y automatiza la aprobación de contenidos.'
  },
  {
    icon: ChartBarIcon,
    title: 'Reporte de impacto real',
    text: 'Alcance, participación y engagement en un solo dashboard.'
  },
  {
    icon: DeviceMobileIcon,
    title: 'Publica en 3 clics',
    text: 'Una app pensada para que tu equipo publique en segundos.'
  }
];

interface TimeSlot {
  time: string;
  zone: string;
}

const TIME_SLOTS: TimeSlot[] = [
  { time: '11:00', zone: 'MX' },
  { time: '12:00', zone: 'COL·PE' },
  { time: '13:00', zone: 'VE·CH' },
  { time: '14:00', zone: 'ARG' }
];

export interface AdvproLaunchModalProps {
  open: boolean;
  /** Cierre con la X: pospone el modal (snooze). */
  onClose: () => void;
  /** Clic en "Ahora no": pospone el modal (snooze). */
  onDismiss: () => void;
  /** Clic en el CTA "Reservar mi lugar gratis" (abre el form en pestaña nueva). */
  onCta: () => void;
  /** Clic en play del video (empieza a reproducir embebido). */
  onVideoPlay?: () => void;
}

export const AdvproLaunchModal: React.FC<AdvproLaunchModalProps> = ({
  open,
  onClose,
  onDismiss,
  onCta,
  onVideoPlay
}) => {
  const [playing, setPlaying] = useState(false);

  if (!open) return null;

  const handlePlay = () => {
    setPlaying(true);
    onVideoPlay?.();
  };

  return (
    <>
      {/* Backdrop (sin cierre al hacer clic: el descarte es explícito) */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(14,23,38,0.6)',
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          zIndex: 99999,
          animation: 'advproFadeIn 0.2s ease'
        }}
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="advproLaunchTitle"
        className="advpro-modal"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 100000,
          width: 'calc(100% - 32px)',
          maxWidth: 940,
          maxHeight: '92vh',
          display: 'flex',
          flexWrap: 'wrap',
          overflowY: 'auto',
          background: '#ffffff',
          borderRadius: 22,
          boxShadow: '0 30px 90px rgba(8,18,40,0.5)',
          color: '#1a1a1a',
          fontFamily: DMSANS,
          animation: 'advproPop 0.32s cubic-bezier(0.16,1,0.3,1)'
        }}
      >
        {/* Close */}
        <button
          className="advpro-close-btn"
          onClick={onClose}
          aria-label="Cerrar"
        >
          <CloseIcon />
        </button>

        {/* Columna izquierda */}
        <div
          style={{
            flex: '1 1 380px',
            minWidth: 300,
            padding: '24px 30px 16px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}
        >
          <img
            src={advocatesProLogo}
            alt="AdvocatesPro"
            style={{
              height: 42,
              width: 'auto',
              alignSelf: 'flex-start',
              marginBottom: 12,
              display: 'block'
            }}
          />

          {/* Badge "webinar en vivo" */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              marginBottom: 12,
              alignSelf: 'flex-start',
              background: 'rgba(230,25,127,0.1)',
              borderRadius: 100,
              padding: '6px 12px 6px 8px'
            }}
          >
            <span
              style={{
                position: 'relative',
                width: 9,
                height: 9,
                borderRadius: '50%',
                background: '#ff3b3b',
                flexShrink: 0
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  inset: -4,
                  borderRadius: '50%',
                  border: '1.5px solid #ff3b3b',
                  animation: 'advproPulse 1.6s ease-out infinite'
                }}
              />
            </span>
            <span
              style={{
                fontFamily: URBANIST,
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: PINK
              }}
            >
              Webinar en vivo · gratis
            </span>
          </div>

          {/* Título */}
          <h1
            id="advproLaunchTitle"
            style={{
              fontFamily: URBANIST,
              fontWeight: 900,
              fontSize: 28,
              lineHeight: 1.08,
              letterSpacing: '-0.01em',
              margin: '0 0 10px',
              color: '#1a1a1a'
            }}
          >
            Tu canal orgánico más potente{' '}
            <span style={{ fontStyle: 'italic', fontWeight: 800, color: PINK }}>
              es tu gente
            </span>
          </h1>

          {/* Subtítulo */}
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.55,
              color: '#3a3a3a',
              margin: 0
            }}
          >
            Conoce nuestra nueva versión, el motor que transforma a tu equipo en
            el <b>canal orgánico más potente de tu empresa.</b>
          </p>

          {/* Fecha y horarios */}
          <div
            style={{
              display: 'flex',
              alignItems: 'stretch',
              gap: 10,
              margin: '18px 0 0'
            }}
          >
            <div
              style={{
                flexShrink: 0,
                width: 56,
                background: '#cdff4f',
                color: '#1a1a1a',
                borderRadius: 12,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: URBANIST
              }}
            >
              <span style={{ fontSize: 22, fontWeight: 900, lineHeight: 1 }}>
                05
              </span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase'
                }}
              >
                Ago
              </span>
            </div>
            <div
              style={{
                flex: 1,
                background: '#f6f5f7',
                borderRadius: 12,
                padding: '9px 12px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 7
              }}
            >
              <span
                style={{
                  fontFamily: URBANIST,
                  fontWeight: 800,
                  fontSize: 11,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: '#6b6470'
                }}
              >
                Miércoles 5 de agosto
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {TIME_SLOTS.map(slot => (
                  <span
                    key={slot.zone}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'baseline',
                      gap: 4,
                      background: '#ffffff',
                      border: '1px solid #ecebee',
                      borderRadius: 7,
                      padding: '3px 7px',
                      fontFamily: URBANIST,
                      fontSize: 11,
                      lineHeight: 1
                    }}
                  >
                    <strong style={{ fontWeight: 900, color: PINK }}>
                      {slot.time}
                    </strong>
                    <span style={{ fontWeight: 700, color: '#1a1a1a' }}>
                      {slot.zone}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Video: poster con play → iframe de YouTube con autoplay */}
          <div
            style={{ marginTop: 18, flex: 1, display: 'flex', minHeight: 150 }}
          >
            <div
              style={{
                position: 'relative',
                width: '100%',
                borderRadius: 14,
                overflow: 'hidden',
                boxShadow: '0 12px 28px rgba(15,23,50,0.16)',
                background: '#000',
                minHeight: 170
              }}
            >
              {playing ? (
                <iframe
                  src={`${ADVPRO_LAUNCH_VIDEO_EMBED_URL}?autoplay=1&rel=0&modestbranding=1&origin=${encodeURIComponent(
                    window.location.origin
                  )}`}
                  title="AdvocatesPro — nueva versión"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    border: 0
                  }}
                  // El sitio manda Referrer-Policy: no-referrer → YouTube da Error 153.
                  // Este override envía solo el origin (no la ruta) para validar el embed.
                  referrerPolicy="strict-origin-when-cross-origin"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <button
                  onClick={handlePlay}
                  aria-label="Reproducir video"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    border: 0,
                    padding: 0,
                    cursor: 'pointer',
                    backgroundImage: `url(${videoPoster})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(36,16,25,0.18)'
                    }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%,-50%)',
                      width: 58,
                      height: 58,
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.95)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 8px 22px rgba(0,0,0,0.35)',
                      paddingLeft: 3
                    }}
                  >
                    <PlayIcon />
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Columna derecha */}
        <div
          style={{
            flex: '1 1 380px',
            minWidth: 300,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div
            style={{
              padding: '26px 26px 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              flex: 1
            }}
          >
            <p
              style={{
                fontFamily: URBANIST,
                fontWeight: 800,
                fontSize: 13,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: '#8b6478',
                margin: 0
              }}
            >
              Qué vas a ver en el webinar
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
                gap: 10,
                minWidth: 0
              }}
            >
              {FEATURES.map(feature => {
                const Icon = feature.icon;
                return (
                  <div
                    key={feature.title}
                    style={{
                      background: '#fdf3f8',
                      border: '1px solid #f7dcea',
                      borderRadius: 12,
                      padding: '12px 13px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                      minWidth: 0
                    }}
                  >
                    <div
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 8,
                        background: '#fbdcec',
                        color: PINK,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 3
                      }}
                    >
                      <Icon />
                    </div>
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: '#241019',
                        lineHeight: 1.25,
                        fontFamily: URBANIST
                      }}
                    >
                      {feature.title}
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        color: '#6e5a64',
                        lineHeight: 1.45
                      }}
                    >
                      {feature.text}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Cupos limitados */}
            <div
              style={{
                background: '#fdf3f8',
                border: '1.5px solid #f2c4dc',
                borderRadius: 12,
                padding: '12px 15px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 11
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  width: 32,
                  height: 32,
                  borderRadius: 9,
                  background: '#fbdcec',
                  color: PINK,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <TicketIcon />
              </div>
              <div style={{ fontSize: 13, color: '#4a2e3a', lineHeight: 1.5 }}>
                <strong
                  style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 700,
                    color: '#241019',
                    marginBottom: 2,
                    fontFamily: URBANIST
                  }}
                >
                  Cupos limitados · entrada gratis
                </strong>
                Actívalo antes que nadie — regístrate y te enviamos la grabación
                si no puedes en vivo.
              </div>
            </div>
          </div>

          {/* CTA + Ahora no */}
          <div
            style={{
              padding: '6px 30px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }}
          >
            <a
              className="advpro-btn-cta"
              href={ADVPRO_LAUNCH_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onCta}
            >
              Reservar mi lugar gratis →
            </a>
            <button className="advpro-btn-later" onClick={onDismiss}>
              Ahora no
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Urbanist:ital,wght@0,600;0,700;0,800;0,900;1,800&family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes advproFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes advproPop { from { opacity: 0; transform: translate(-50%, -47%) scale(0.985) } to { opacity: 1; transform: translate(-50%, -50%) scale(1) } }
        @keyframes advproPulse { 0% { transform: scale(0.7); opacity: 1 } 100% { transform: scale(1.8); opacity: 0 } }
        .advpro-modal::-webkit-scrollbar { width: 0; }
        .advpro-close-btn{position:absolute;top:14px;right:14px;width:30px;height:30px;z-index:5;border-radius:50%;border:none;background:rgba(0,0,0,0.28);color:rgba(255,255,255,0.9);display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0;transition:background 0.15s;}
        .advpro-close-btn:hover{background:rgba(0,0,0,0.45);}
        .advpro-close-btn:focus-visible{outline:2px solid ${PINK};outline-offset:2px;}
        .advpro-btn-cta{flex:1;display:block;text-decoration:none;text-align:center;background:${PINK};color:#ffffff;font-family:${URBANIST};font-weight:800;font-size:15.5px;padding:14px 24px;border-radius:12px;cursor:pointer;box-shadow:0 10px 24px rgba(230,25,127,0.35);transition:transform 0.1s,box-shadow 0.15s,background 0.15s;}
        .advpro-btn-cta:hover{transform:translateY(-1px);box-shadow:0 12px 28px rgba(230,25,127,0.45);background:${PINK_DARK};}
        .advpro-btn-cta:focus-visible{outline:2px solid ${PINK_DARK};outline-offset:3px;}
        .advpro-btn-later{flex-shrink:0;background:#ffffff;border:1px solid #ecebee;color:#6b6470;font-family:${URBANIST};font-weight:700;font-size:13.5px;padding:14px 16px;border-radius:12px;cursor:pointer;text-align:center;transition:background 0.15s,color 0.15s,border-color 0.15s;}
        .advpro-btn-later:hover{background:#f6f5f7;color:#1a1a1a;border-color:#d9d6dc;}
      `}</style>
    </>
  );
};

export default AdvproLaunchModal;
